import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { RouteBadge } from '@/features/route/RouteBadge';
import { getMeeting, updateMeetingPoint } from '@/shared/api/meetings';
import { listScheduleItems } from '@/shared/api/schedule';
import { listPlaceCandidates } from '@/shared/api/voting';
import { apiErrorMessage } from '@/shared/api/client';
import { useAuthStore } from '@/shared/auth/store';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Icon } from '@/shared/ui/Icon';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';

function kakaoDirectionUrl(name: string, x?: string | null, y?: string | null) {
  if (!x || !y) return null;
  return `https://map.kakao.com/link/to/${encodeURIComponent(name)},${y},${x}`;
}

export default function MeetingPointPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);

  const meetingQ = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => getMeeting(id),
    enabled: !!id,
  });
  const scheduleQ = useQuery({
    queryKey: ['schedule', id],
    queryFn: () => listScheduleItems(id),
    enabled: !!id,
  });
  const placesQ = useQuery({
    queryKey: ['places', id],
    queryFn: () => listPlaceCandidates(id),
    enabled: !!id,
  });

  const firstConfirmedPlace = useMemo(() => {
    const items = scheduleQ.data ?? [];
    const placesById = new Map((placesQ.data ?? []).map((place) => [place.id, place]));
    for (const item of items) {
      if (!item.placeCandidateId) continue;
      const place = placesById.get(item.placeCandidateId);
      if (place?.confirmed && place.x && place.y) return place;
    }
    return null;
  }, [placesQ.data, scheduleQ.data]);

  const setFirstPlaceMut = useMutation({
    mutationFn: () => {
      if (!firstConfirmedPlace?.x || !firstConfirmedPlace.y) throw new Error('no first place');
      return updateMeetingPoint(id, {
        name: firstConfirmedPlace.name,
        x: firstConfirmedPlace.x,
        y: firstConfirmedPlace.y,
      });
    },
    onSuccess: (meeting) => {
      qc.setQueryData(['meeting', id], meeting);
      toast.success('첫 장소를 집결지로 설정했어요.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, '집결지를 설정하지 못했어요.')),
  });

  if (meetingQ.isLoading) return <SkeletonCard />;
  if (meetingQ.isError || !meetingQ.data) {
    return <ErrorState title="집결지를 불러오지 못했어요" onRetry={() => meetingQ.refetch()} />;
  }

  const meeting = meetingQ.data;
  const isHost = user?.id === meeting.hostUserId;
  const hasPoint = Boolean(meeting.meetingPointName && meeting.meetingPointX && meeting.meetingPointY);
  const pointName = meeting.meetingPointName ?? firstConfirmedPlace?.name ?? meeting.region ?? '집결지 미정';
  const pointX = meeting.meetingPointX ?? firstConfirmedPlace?.x ?? null;
  const pointY = meeting.meetingPointY ?? firstConfirmedPlace?.y ?? null;
  const directionsUrl = kakaoDirectionUrl(pointName, pointX, pointY);

  return (
    <section className="space-y-4 bg-canvas pb-4">
      <h1 className="sr-only">집결지</h1>
      <MeetingTabs meetingId={id} />

      <section className="space-y-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-tag font-semibold uppercase tracking-[0.04em] text-primary">Gathering</p>
            <h2 className="mt-0.5 text-[22px] font-semibold leading-tight tracking-[-0.6px] text-ink">집결지</h2>
          </div>
          <span className="rounded-full bg-primary-soft px-2.5 py-1 text-[11px] font-semibold text-primary">
            {hasPoint ? '확정' : firstConfirmedPlace ? '첫 장소 사용 가능' : '설정 필요'}
          </span>
        </div>

        <Card className="!overflow-hidden !border-surface-border !p-0">
          <div className="relative h-24 bg-gradient-to-br from-primary-soft via-[#e9f8e4] to-success-soft">
            <div className="absolute left-[-24px] top-8 h-3.5 w-44 rotate-[-18deg] rounded-full bg-primary-light/35" />
            <div className="absolute right-[-28px] top-14 h-3.5 w-40 rotate-[22deg] rounded-full bg-primary-light/35" />
            <div className="absolute left-1/2 top-8 h-11 w-11 -translate-x-1/2 rotate-[-45deg] rounded-[50%_50%_50%_4px] bg-primary shadow-card">
              <div className="absolute inset-3 rounded-full bg-surface" />
            </div>
          </div>
          <div className="p-4">
            <p className="text-[12px] font-medium text-ink-faint">
              {hasPoint ? '현재 집결지' : firstConfirmedPlace ? '첫 확정 장소' : '아직 없음'}
            </p>
            <h3 className="mt-1 text-[21px] font-semibold leading-tight tracking-[-0.5px] text-ink">{pointName}</h3>
            <p className="mt-1 text-body text-ink-muted">
              {hasPoint
                ? '코스와 지도 검색의 시작점으로 사용해요.'
                : firstConfirmedPlace
                  ? '별도 집결지 없이 첫 장소에서 바로 만나려면 설정하세요.'
                  : '지도에서 집결지를 정하거나, 첫 일정 장소를 확정하면 바로 집결할 수 있어요.'}
            </p>

            {hasPoint && firstConfirmedPlace?.x && firstConfirmedPlace.y && pointX && pointY ? (
              <div className="mt-3 rounded-2xl bg-surface-muted px-3 py-2">
                <p className="text-[11px] font-semibold text-ink-faint">첫 장소까지</p>
                <RouteBadge
                  origin={{ x: pointX, y: pointY }}
                  dest={{ x: firstConfirmedPlace.x, y: firstConfirmedPlace.y }}
                  initialMode="walk"
                />
              </div>
            ) : null}

            {hasPoint ? (
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button
                  variant="brand"
                  className="gap-1.5"
                  onClick={() => directionsUrl && window.open(directionsUrl, '_blank', 'noopener,noreferrer')}
                  disabled={!directionsUrl}
                >
                  <Icon name="map" size={16} />
                  길찾기
                </Button>
                {isHost ? (
                  <Button
                    variant="secondary"
                    className="gap-1.5"
                    onClick={() => navigate(`/meetings/${id}/places?for=meeting-point`)}
                  >
                    <Icon name="pin" size={16} />
                    변경
                  </Button>
                ) : (
                  <Link
                    to={`/meetings/${id}`}
                    className="pressable inline-flex min-h-11 items-center justify-center rounded-xl bg-surface-muted px-4 text-small font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
                  >
                    요약으로
                  </Link>
                )}
              </div>
            ) : (
              <Button
                variant="brand"
                className="mt-4 w-full gap-1.5"
                onClick={() => navigate(`/meetings/${id}/places?for=meeting-point`)}
              >
                <Icon name="pin" size={16} />
                집결지 선택
              </Button>
            )}
          </div>
        </Card>
      </section>

      {isHost && !hasPoint && firstConfirmedPlace ? (
        <button
          type="button"
          onClick={() => setFirstPlaceMut.mutate()}
          disabled={setFirstPlaceMut.isPending}
          className="pressable flex w-full items-center justify-between gap-3 rounded-2xl border border-primary/25 bg-primary-soft px-4 py-3 text-left focus-visible:outline-none focus-visible:shadow-focus-ring disabled:opacity-50"
        >
          <span className="min-w-0">
            <span className="block text-[14px] font-semibold text-ink">첫 장소로 바로 집결</span>
            <span className="mt-0.5 block truncate text-small text-ink-muted">{firstConfirmedPlace.name}</span>
          </span>
          <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-[12px] font-semibold text-white">
            설정
          </span>
        </button>
      ) : null}

    </section>
  );
}
